import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Mail, MessageSquare } from 'lucide-react';

import type { CourierOtpMethod } from 'features/courier-auth/services/courierAuthService';

interface ChannelStepProps {
  selected: CourierOtpMethod;
  availableMethods: CourierOtpMethod[];
  maskedPhone?: string;
  maskedEmail?: string;
  isSubmitting: boolean;
  errorMessage: string | null;
  onSelect: (method: CourierOtpMethod) => void;
  onBack: () => void;
  onContinue: () => void;
}

const ChannelStep: React.FC<ChannelStepProps> = ({
  selected,
  availableMethods,
  maskedPhone,
  maskedEmail,
  isSubmitting,
  errorMessage,
  onSelect,
  onBack,
  onContinue,
}) => {
  const { t } = useTranslation();

  const options: Array<{
    method: CourierOtpMethod;
    Icon: React.FC<{ className?: string }>;
    titleKey: string;
    hint: string;
  }> = [];

  if (availableMethods.includes('sms')) {
    options.push({
      method: 'sms',
      Icon: MessageSquare,
      titleKey: 'courierAuth.channel.sms',
      hint: t('courierAuth.channel.smsHint', {
        target: maskedPhone || t('courierAuth.channel.yourPhone'),
      }),
    });
  }
  if (availableMethods.includes('email')) {
    options.push({
      method: 'email',
      Icon: Mail,
      titleKey: 'courierAuth.channel.email',
      hint: t('courierAuth.channel.emailHint', {
        target: maskedEmail || t('courierAuth.channel.yourEmail'),
      }),
    });
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl">
          {t('courierAuth.channel.heading')}
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-gray-600">
          {t('courierAuth.channel.subheading')}
        </p>
      </div>

      <div
        role="radiogroup"
        aria-label={t('courierAuth.channel.heading') as string}
        className="space-y-3"
      >
        {options.map(({ method, Icon, titleKey, hint }) => {
          const isSelected = selected === method;
          return (
            <button
              key={method}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => onSelect(method)}
              disabled={isSubmitting}
              className={`flex w-full items-center gap-3 rounded-xl border p-4 text-start transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#bf0035] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${
                isSelected
                  ? 'border-[#bf0035] bg-[#bf0035]/[0.035]'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <span
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                  isSelected
                    ? 'bg-[#bf0035]/[0.07] text-[#bf0035]'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                <Icon className="h-5 w-5" />
              </span>
              <span className="flex-1">
                <span className="block font-semibold text-gray-900">{t(titleKey)}</span>
                <span className="block text-xs text-gray-500 mt-0.5" dir="ltr">
                  {hint}
                </span>
              </span>
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                  isSelected ? 'border-[#bf0035]' : 'border-gray-300'
                }`}
              >
                {isSelected ? (
                  <span className="h-2.5 w-2.5 rounded-full bg-[#bf0035]" />
                ) : null}
              </span>
            </button>
          );
        })}
      </div>

      {errorMessage && (
        <div className="border-s-2 border-red-600 bg-red-50 px-4 py-3 text-sm text-red-700">
          {errorMessage}
        </div>
      )}

      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="btn-outline flex-1 disabled:opacity-50"
        >
          {t('common.back')}
        </button>

        <button
          type="button"
          onClick={onContinue}
          disabled={isSubmitting || options.length === 0}
          className="btn-primary flex flex-[2] items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            t('courierAuth.channel.continue')
          )}
        </button>
      </div>
    </div>
  );
};

export default ChannelStep;
